import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import api from '../api/axios';
import { jwtDecode } from 'jwt-decode';
import Calendario from '../components/Calendario';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useSearchParams } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as Sentry from '@sentry/react';

export default function Reservar() {
  const [paso, setPaso] = useState(1);
  const [cargando, setCargando] = useState(false);
  const [ajustes, setAjustes] = useState({ dias_maximos_reserva: 28 });

  const [servicios, setServicios] = useState([]);
  const [form, setForm] = useState({
    servicioId: '',
    fecha: '',
    hora: '',
    nombre: '',
    apellidos: '',
    telefono: '',
    email: ''
  });
  const [excepciones, setExcepciones] = useState([]);
  const [horariosDisponibles, setHorariosDisponibles] = useState([]);
  const [diasDisponibles, setDiasDisponibles] = useState([]);
  const [mensajeError, setMensajeError] = useState('');

  const resumenRef = useRef(null);
  const [proximoHueco, setProximoHueco] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(() => {
    try {
      return localStorage.getItem('hideWarningPrecios') !== '1';
    } catch (e) {
      return true;
    }
  });

  const validarTelefono = (telefono) => {
    const telefonoObj = parsePhoneNumberFromString(telefono, 'ES'); // o 'FR', 'US'...
    return telefonoObj?.isValid() ?? false;
  };
  
  // Cargar ajustes iniciales
  useEffect(() => {
    api.get('/ajustes')
      .then(res => setAjustes(res.data))
      .catch(err => {
        console.error('❌ Error al cargar ajustes:', err);
        setAjustes({ dias_maximos_reserva: 28 });
      });
  }, []);

  // Prellenar servicio desde URL
  useEffect(() => {
    const servicioFromUrl = searchParams.get('id');
    if (servicioFromUrl) {
      setForm(prev => ({ ...prev, servicioId: servicioFromUrl }));
    }
  }, [searchParams]);

  // Cargar lista de servicios, horarios y excepciones, y usuario autenticado
  useEffect(() => {
    api.get('/servicios')
      .then(res => setServicios(res.data.servicios))
      .catch(err => console.error('❌ Error al cargar servicios:', err));

    

    api.get('/excepciones')
      .then(res => {
        const arr = Object.entries(res.data.excepciones).flatMap(([fecha, tramos]) =>
          tramos.map(t => ({ ...t, fecha }))
        );
        setExcepciones(arr);
      })
      .catch(err => console.error('❌ Error al cargar excepciones:', err));

    const token = localStorage.getItem('token');
    if (token) {
      try {
        const usuario = jwtDecode(token);
        setForm(prev => ({
          ...prev,
          nombre: usuario.nombre,
          apellidos: usuario.apellidos,
          telefono: usuario.telefono,
          email: usuario.email
        }));
      } catch (err) {
        console.error('❌ Error decodificando token:', err);
      }
    }
  }, []);

  // Consultar días disponibles del mes para el calendario
  useEffect(() => {
    if (!form.servicioId) {
      setDiasDisponibles([]);
      return;
    }
    const hoy = new Date();
    const mes = hoy.getMonth() + 1;
    const anio = hoy.getFullYear();
    const mesStr = mes.toString().padStart(2, '0');

    api.get(`/disponibilidad/mes/${anio}-${mesStr}/${form.servicioId}`)
      .then(res => setDiasDisponibles(res.data.diasDisponibles || []))
      .catch(err => {
        console.error('❌ Error al cargar días disponibles:', err);
        setDiasDisponibles([]);
      });
  }, [form.servicioId]);

  // Consultar slots por fecha
  useEffect(() => {
    if (form.servicioId && form.fecha) {
      api.get(`/disponibilidad/${form.fecha}/${form.servicioId}`)
        .then(res => setHorariosDisponibles(res.data.huecos))
        .catch(() => setHorariosDisponibles([]));
    }
  }, [form.servicioId, form.fecha]);

  // Consultar próximo hueco
  useEffect(() => {
    if (form.servicioId) {
      api.get(`/disponibilidad/proximo/${form.servicioId}`)
        .then(res => setProximoHueco(res.data))
        .catch(() => setProximoHueco(null));
    }
  }, [form.servicioId]);

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFechaSeleccionada = fecha => {
    setForm({ ...form, fecha });
    setForm(prev => ({ ...prev, hora: '' }));
  };

  const cambiarPaso = nuevo => {
    setPaso(nuevo);
  };
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [paso]);
  

  const confirmarReserva = async () => {
    setCargando(true);
    try {
      const servicioSel = servicios.find(s => s.id === parseInt(form.servicioId));
      const precio = servicioSel?.precio || 0;
      await api.post('/reservar', {
        ...form,
        hora_inicio: form.hora,
        precio_cita: precio
      });
      navigate('/confirmacion');
    } catch (err) {
      console.error('❌ Error al reservar:', err);
      toast.error('Error al reservar la cita');
      Sentry.captureException(err); 
      setCargando(false);
    }
  };

  const hideWarning = () => {
    try {
      localStorage.setItem('hideWarningPrecios', '1');
    } catch (e) {
      // noop
    }
    setShowWarning(false);
  };

  const capitalizar = str => str.charAt(0).toUpperCase() + str.slice(1);

  return (
    <main>
      <div className="grid-reservas site-width">
        <h2>Reserva tu cita</h2>
        <p>Reservar tu cita es gratis. Solo pagarás tu cita presencialmente cuando tu servicio finalice.</p>

        <div className="barra-progreso-reserva" style={{ display: 'flex', justifyContent: 'space-between' }}>
          {['Servicio','Hora','Datos','Resumen'].map((label,i) => {
            const num = i+1;
            const activo = paso===num || paso>num;
            return (
              <div key={i} style={{ flex:1, textAlign:'center' }}>
                <div style={{ height:'6px', backgroundColor: activo? '#244480':'#ccc', marginBottom:'5px', transition:'background-color 0.3s' }} />
                <small style={{ color: activo? '#244480':'#999' }}>{label}</small>
              </div>
            );
          })}
        </div>

        {paso===1 && (
          <div className="grid-reservas-pasouno">
            <p>Selecciona tu servicio:</p>
            {showWarning && (
              <div className="warning-precios" role="region" aria-live="polite">
                <div style={{ flex: 1 }}>
                  <p>¡Atención! He actualizado mis precios para seguir garantizando la máxima calidad en cada detalle, gracias por tu tiempo.</p>
                </div>
                <button
                  type="button"
                  className="warning-precios-close"
                  onClick={hideWarning}
                  aria-label="Cerrar aviso de precios"
                >
                  Entendido
                </button>
              </div>
            )}
            <div className="servicios-grid">
              {servicios.map(s => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, servicioId: String(s.id) }))}
                  className={form.servicioId === String(s.id) ? 'servicio-btn selected' : 'servicio-btn'}
                  style={{
                    border: form.servicioId === String(s.id) ? '2px solid #25d366' : '1px solid #ccc',
                    borderRadius: '5px',
                    background: 'white',
                    cursor: 'pointer'
                  }}
                >
                  <span>{s.nombre}</span>
                  <p>{s.duracion} mins. - {s.precio}€ </p>
                </button>
              ))}
            </div>

            {proximoHueco && (
              <div className="proxima-cita" style={{ marginBottom:'1.5rem', padding:'1rem', backgroundColor:'#eef6f9', borderRadius:'8px', textAlign:'center' }}>
                <strong>Próxima cita disponible:</strong><br />
                {capitalizar(
                  format(
                    new Date(`${proximoHueco.fecha}T${proximoHueco.hora}`),
                    "eeee, d 'de' MMMM 'a las' HH:mm aaaa",
                    { locale: es }
                  )
                )}
              </div>
            )}
            <div className='calendario-container'>
              {
                form.servicioId ? '' : 
                <>
                <div className='overlay-calendario'>
                  <p>Debes seleccionar un servicio para consultar disponibilidad</p>
                </div>
                </>
              }
            <Calendario
              fechaSeleccionada={form.fecha}
              onSeleccionarFecha={handleFechaSeleccionada}
              servicioId={form.servicioId}
              diasMaximos={ajustes.dias_maximos_reserva}
              diasDisponibles={diasDisponibles}
              excepcionesHorarias={excepciones.map(e => e.fecha)} 
            />
            </div>
            <button
              className={!form.servicioId || !form.fecha ? 'boton-siguiente boton-disabled' : 'boton-siguiente'}
              disabled={!form.servicioId || !form.fecha}
              onClick={() => cambiarPaso(2)}
            >Siguiente</button>
          </div>
        )}

{paso === 2 && (
  <div className='grid-reservas-pasodos'>
    <h3>¿A qué hora te gustaría reservar?</h3>

    {/* --- BLOQUE MAÑANA --- */}
    <div className="horario-manana">
      <h4>Horario de mañana</h4>
      {horariosDisponibles.some(h => h <= '14:30') ? (
        <div className='grid-horas'>
          {horariosDisponibles
            .filter(h => h <= '14:30')
            .map((hora, i) => (
              <div
                key={`manana-${i}`}
                onClick={() => setForm({ ...form, hora })}
                className={form.hora === hora ? 'hora-selected' : 'hora'}
                style={{ padding: '8px 12px', cursor: 'pointer' }}
              >
                {hora}
              </div>
          ))}
        </div>
      ) : (
        <p className='no-horario-disponible'>No hay horarios de mañana disponibles.</p>
      )}
    </div>

    {/* --- BLOQUE TARDE --- */}
    <div className="horario-tarde">
      <h4>Horario de tarde</h4>
      {horariosDisponibles.some(h => h > '14:30') ? (
        <div className='grid-horas'>
          {horariosDisponibles
            .filter(h => h > '14:30')
            .map((hora, i) => (
              <div
                key={`tarde-${i}`}
                onClick={() => setForm({ ...form, hora })}
                className={form.hora === hora ? 'hora-selected' : 'hora'}
                style={{ padding: '8px 12px', cursor: 'pointer' }}
              >
                {hora}
              </div>
          ))}
        </div>
      ) : (
        <p className='no-horario-disponible'>No hay horarios de tarde disponibles.</p>
      )}
    </div>

    {/* --- BOTONES --- */}
    <div className="acciones-reserva">
      <button className='boton-atras' onClick={() => cambiarPaso(1)}>Atrás</button>
      <button
        className={!form.servicioId || !form.hora ? 'boton-siguiente boton-disabled' : 'boton-siguiente'}
        disabled={!form.hora}
        onClick={() => cambiarPaso(3)}
      >
        Siguiente
      </button>
    </div>
  </div>
)}


        {paso === 3 && (
          <form
            className='form-datos-personales'
            onSubmit={(e) => {
              e.preventDefault();

              const errores = [];
              if (!form.nombre.trim()) errores.push('nombre');
              if (!form.apellidos.trim()) errores.push('apellidos');
              if (!form.telefono.trim() || !validarTelefono(form.telefono)) {
                errores.push('teléfono (número no válido)');
              }
               
              
              const check1 = document.getElementById('condiciones');
              const check2 = document.getElementById('datos-correctos');

              if (!check1?.checked) errores.push('condiciones');
              if (!check2?.checked) errores.push('política de servicio');

              if (errores.length > 0) {
                setMensajeError(`Faltan por rellenar o aceptar: ${errores.join(', ')}.`);
                document.getElementById('mensaje-error')?.scrollIntoView({ behavior: 'smooth' });
                return;
              }

              setMensajeError('');
              cambiarPaso(4);
            }}
          >
            <div className="grupo-uno-form">
              <div className="label-form">
                <label htmlFor="nombre">Nombre <span className='obligatorio'>*</span></label>
                <input name="nombre" placeholder="Nombre" value={form.nombre} onChange={handleChange} />
              </div>
              <div className="label-form">
                <label htmlFor="apellidos">Apellidos <span className='obligatorio'>*</span></label>
                <input name="apellidos" placeholder="Apellidos" value={form.apellidos} onChange={handleChange} />
              </div>
              <div className="label-form">
                <label htmlFor="telefono">Teléfono <span className='obligatorio'>*</span></label>
                <PhoneInput
                  country={'es'}
                  value={form.telefono}
                  onChange={(value) => setForm({ ...form, telefono: value })}
                />
              </div>
              <div className="label-form">
                <label htmlFor="email">Correo electrónico <span className='opcional'>(opcional)</span></label>
                <input name="email" type="email" placeholder="Email" value={form.email} onChange={handleChange} />
              </div>
            </div>

            <div className="grupo-dos-form">
              <div className="checkbox-form">
                <label>
                  <input type="checkbox" id="condiciones" />
                  <svg viewBox="0 0 64 64" height="16px" width="16px">
                  <path d="M 0 16 V 56 A 8 8 90 0 0 8 64 H 56 A 8 8 90 0 0 64 56 V 8 A 8 8 90 0 0 56 0 H 8 A 8 8 90 0 0 0 8 V 16 L 32 48 L 64 16 V 8 A 8 8 90 0 0 56 0 H 8 A 8 8 90 0 0 0 8 V 56 A 8 8 90 0 0 8 64 H 56 A 8 8 90 0 0 64 56 V 16" pathLength="575.0541381835938" class="path"></path>
                  </svg>
                </label>
                <p>He leído y acepto las <a href="/condiciones-del-sistema-de-citas" target="_blank" rel="noopener noreferrer">condiciones del sistema de citas</a>.</p>
              </div>
              <div className="checkbox-form">
                <label>
                  <input type="checkbox" id="datos-correctos" />
                  <svg viewBox="0 0 64 64" height="16px" width="16px">
                  <path d="M 0 16 V 56 A 8 8 90 0 0 8 64 H 56 A 8 8 90 0 0 64 56 V 8 A 8 8 90 0 0 56 0 H 8 A 8 8 90 0 0 0 8 V 16 L 32 48 L 64 16 V 8 A 8 8 90 0 0 56 0 H 8 A 8 8 90 0 0 0 8 V 56 A 8 8 90 0 0 8 64 H 56 A 8 8 90 0 0 64 56 V 16" pathLength="575.0541381835938" class="path"></path>
                  </svg>
                </label>
                <p>He leído y acepto la <a href="/politica-de-servicio" target="_blank" rel="noopener noreferrer">política de servicio</a> y <a href='/politica-de-privacidad' target="_blank" rel="noopener noreferrer">política de privacidad</a> del sitio web.</p>
              </div>
            </div>

            {mensajeError && <p id="mensaje-error" className="mensaje-error">{mensajeError}</p>}

            <div className="acciones-reserva">
              <button className='boton-atras' onClick={() => cambiarPaso(2)}>Atrás</button>
              <button className='boton-siguiente' type="submit">Siguiente</button>
            </div>
          </form>
        )}

        {paso === 4 && (
          <div className='resumen-reserva' ref={resumenRef}>
          <h3>{form.nombre}, estás a punto de confirmar tu reserva.</h3>
          <p>¿Son correctos estos datos?</p>
          <div className="resumen-datos">
            <div className="datos-card">
              <img src="/images/formulario.svg" alt="Icono" />
              <div className="texto-datos-card">
                <strong>Nombre</strong> 
                <p>{form.nombre} {form.apellidos}</p>
              </div>
            </div>
            <div className="datos-card">
              <img src="/images/telefono-negro.svg" alt="Icono" />
              <div className="texto-datos-card">
                <strong>Teléfono</strong>
                <p>+{form.telefono}</p>
              </div>
            </div>

            <div className="datos-card">
              <img src="/images/email-usuario.svg" alt="Icono" />
              <div className="texto-datos-card">
                  {form.email ? (
                    <>
                    <strong>Email</strong>
                    <p>{form.email}</p>
                   </>
                ) : (
                  <>
                  <strong>Email</strong>
                    <p style={{ color: 'red' }}>
                     No has indicado un correo electrónico, por lo que no podremos enviarte notificaciones sobre tus citas (confirmaciones, cambios o cancelaciones).
                  </p>
                  </>

                )}
              </div>
            </div>
            <div className="datos-card">
              <img src="/images/tijeras.svg" alt="Icono" />
              <div className="texto-datos-card">
                <strong>Servicio</strong>
                <p> {servicios.find(s => s.id === parseInt(form.servicioId))?.nombre} - ({servicios.find(s => s.id === parseInt(form.servicioId))?.precio}€)</p>
              </div>
            </div>
            <div className="datos-card">
              <img src="/images/lineas-de-calendario.svg" alt="Icono" />
              <div className="texto-datos-card">
                <strong>Fecha</strong>
                <p>
                    {' '}
                    {capitalizar(
                      format(
                        new Date(`${form.fecha}T${form.hora}`),
                        "eeee, d 'de' MMMM 'a las' HH:mm",
                        { locale: es }
                      )
                    ) + 'h'}
                  </p>
              </div>
            </div>
          </div>

            <div className="acciones-reserva">
              <button className='boton-atras' onClick={() => cambiarPaso(3)}>Atrás</button>
              <button className='boton-confirmar' onClick={confirmarReserva} disabled={cargando}>
                {cargando ? 'Cargando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        )}
      </div>
      <ToastContainer position="bottom-right" autoClose={3000} />
    </main>
  );
}